'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import useSWR from 'swr';
import { apiFetch, swrFetcher } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { useTranslation } from '../../../lib/i18n';
import { useEshopCart } from '../../../lib/useEshopCart';
import { useToast } from '../../../components/Toast';
import Alert from '../../../components/Alert';
import LoadingFallback from '../../../components/LoadingFallback';

export const dynamic = 'force-dynamic';

type Variant = {
  id: number;
  name: string;
  sku: string | null;
  price: number | null;
  stockQuantity: number | null;
  isActive: boolean;
};

type Review = {
  id: number;
  authorName: string | null;
  rating: number;
  body: string | null;
  createdAt: number[] | string;
};

type Product = {
  id: number;
  name: string;
  slug: string;
  shortDescription: string | null;
  description: string | null;
  sku: string | null;
  price: number | null;
  groupPrice: number | null;
  priceGroupName: string | null;
  currency: string;
  stockQuantity: number | null;
  imageAssetId: number | null;
  galleryAssetIds: number[] | null;
  isFeatured: boolean;
  categoryId: number | null;
  categorySlug: string | null;
  categoryName: string | null;
  attributes: Record<string, unknown> | null;
  variants: Variant[] | null;
  avgRating: number | null;
  reviewCount: number | null;
  related: Product[] | null;
};

const fmt = (n: number, c: string) =>
  `${n.toLocaleString('cs-CZ', { minimumFractionDigits: 2 })} ${c || 'CZK'}`;

const fmtDate = (v: number[] | string) => {
  try {
    if (Array.isArray(v)) {
      const [y, m, d] = v;
      return new Date(y, m - 1, d).toLocaleDateString('cs-CZ');
    }
    return new Date(v).toLocaleDateString('cs-CZ');
  } catch {
    return '';
  }
};

function Stars({ value, onChange }: { value: number; onChange?: (v: number) => void }) {
  return (
    <span className="inline-flex gap-0.5 text-lg">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={!onChange}
          onClick={() => onChange?.(i)}
          className={i <= Math.round(value) ? 'text-amber-500' : 'text-wheat'}
        >
          ★
        </button>
      ))}
    </span>
  );
}

export default function ShopProductPage() {
  const { t } = useTranslation();
  const params = useParams();
  const slug = params?.slug as string;
  const [qty, setQty] = useState(1);
  const [variantId, setVariantId] = useState<number | null>(null);
  const { showToast } = useToast();
  const { isAuthenticated } = useAuth();

  const { data: settings } = useSWR<{ features?: { cart?: boolean; reviews?: boolean; wishlist?: boolean } }>(
    '/api/public/eshop/settings', swrFetcher);
  const cartEnabled = settings?.features?.cart === true;
  const reviewsEnabled = settings?.features?.reviews === true;
  const wishlistEnabled = settings?.features?.wishlist === true;
  const { addItem } = useEshopCart(cartEnabled);

  const { data: p, error, isLoading } = useSWR<Product>(
    slug ? `/api/public/eshop/products/${slug}` : null,
    swrFetcher
  );

  const { data: reviews, mutate: mutateReviews } = useSWR<Review[]>(
    reviewsEnabled && p ? `/api/public/eshop/products/${p.id}/reviews` : null,
    swrFetcher
  );

  const { data: wishlistIds, mutate: mutateWishlist } = useSWR<number[]>(
    wishlistEnabled && isAuthenticated ? '/api/public/eshop/wishlist/ids' : null,
    swrFetcher
  );

  // Recently viewed — read once from localStorage; the effect below only writes.
  type RecentItem = { slug: string; name: string; price: number | null; currency: string; imageAssetId: number | null };
  const [recent] = useState<RecentItem[]>(() => {
    try { return JSON.parse(localStorage.getItem('eshop_recently_viewed') || '[]'); }
    catch { return []; }
  });
  useEffect(() => {
    if (!p) return;
    try {
      const KEY = 'eshop_recently_viewed';
      const cur = JSON.parse(localStorage.getItem(KEY) || '[]') as RecentItem[];
      const next = [
        { slug: p.slug, name: p.name, price: p.groupPrice ?? p.price, currency: p.currency, imageAssetId: p.imageAssetId },
        ...cur.filter((r) => r.slug !== p.slug),
      ].slice(0, 8);
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch { /* localStorage unavailable */ }
  }, [p]);

  if (isLoading) return <LoadingFallback />;
  if (error || !p) return <Alert type="error">{t('eshopProductNotFound')}</Alert>;

  const variants = (p.variants || []).filter((v) => v.isActive !== false);
  const selectedVariant = variants.find((v) => v.id === variantId) || null;
  const hasVariants = variants.length > 0;

  const effectivePrice = selectedVariant?.price ?? p.groupPrice ?? p.price;
  const effectiveStock = selectedVariant ? selectedVariant.stockQuantity : p.stockQuantity;
  const effectiveSku = selectedVariant?.sku || p.sku;

  const gallery = [p.imageAssetId, ...(p.galleryAssetIds || [])].filter((x): x is number => x != null);
  const onWishlist = p ? (wishlistIds || []).includes(p.id) : false;

  const toggleWishlist = async () => {
    try {
      await apiFetch(`/api/public/eshop/wishlist/${p.id}`, { method: 'POST' });
      mutateWishlist();
    } catch {
      showToast(t('eshopWishlistError'), 'error');
    }
  };

  const canAddToCart = !hasVariants || selectedVariant != null;

  return (
    <div>
      <nav className="text-sm text-wood mb-5">
        <Link href="/shop" className="hover:text-bark">{t('eshopCatalog')}</Link>
        {p.categoryName && (
          <>
            <span className="mx-1.5">/</span>
            <span>{p.categoryName}</span>
          </>
        )}
        <span className="mx-1.5">/</span>
        <span className="text-bark">{p.name}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-linen border border-wheat rounded-rustic overflow-hidden flex items-center justify-center">
            {gallery.length > 0 ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={`/api/public/eshop/assets/${gallery[0]}`}
                alt={p.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-8xl text-wheat">🛍️</span>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {gallery.map((id) => (
                <div key={id} className="aspect-square bg-linen border border-wheat rounded overflow-hidden">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={`/api/public/eshop/assets/${id}`} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-2xl font-semibold text-bark">{p.name}</h1>
            {wishlistEnabled && isAuthenticated && (
              <button
                onClick={toggleWishlist}
                className={`text-2xl leading-none ${onWishlist ? 'text-rust' : 'text-wheat hover:text-wood'}`}
                title={t('eshopWishlist')}
              >
                ♥
              </button>
            )}
          </div>
          {effectiveSku && <div className="text-xs text-wood mt-1">{t('eshopSku')}: {effectiveSku}</div>}

          {reviewsEnabled && p.reviewCount != null && p.reviewCount > 0 && (
            <div className="mt-1 flex items-center gap-2 text-sm">
              <Stars value={p.avgRating ?? 0} />
              <span className="text-wood">({p.reviewCount})</span>
            </div>
          )}

          {effectivePrice == null && p.groupPrice == null ? (
            <div className="mt-4 text-sm text-wood">{t('eshopPriceOnLogin')}</div>
          ) : !selectedVariant && p.groupPrice != null && p.price != null && p.groupPrice < p.price ? (
            <div className="mt-4">
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold text-moss">
                  {p.groupPrice.toLocaleString('cs-CZ', { minimumFractionDigits: 2 })} {p.currency}
                </span>
                <span className="text-lg text-wood line-through">
                  {p.price.toLocaleString('cs-CZ', { minimumFractionDigits: 2 })} {p.currency}
                </span>
              </div>
              {p.priceGroupName && (
                <div className="text-xs text-moss mt-0.5">{t('eshopYourPrice')} · {p.priceGroupName}</div>
              )}
            </div>
          ) : (
            <div className="mt-4 text-3xl font-bold text-bark">
              {(effectivePrice ?? 0).toLocaleString('cs-CZ', { minimumFractionDigits: 2 })} {p.currency}
            </div>
          )}

          {effectiveStock != null && (
            <div className={`mt-2 text-sm ${effectiveStock > 0 ? 'text-moss' : 'text-rust'}`}>
              {effectiveStock > 0
                ? `${t('eshopInStock')} (${effectiveStock} ${t('eshopPieces')})`
                : t('eshopOutOfStock')}
            </div>
          )}

          {hasVariants && (
            <div className="mt-4">
              <div className="text-sm text-wood mb-1.5">{t('eshopVariant')}:</div>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setVariantId(v.id === variantId ? null : v.id)}
                    className={`px-3 py-1.5 text-sm rounded-rustic border transition-colors ${
                      v.id === variantId
                        ? 'border-moss bg-moss text-cream'
                        : 'border-wheat bg-linen text-ink hover:border-wood'
                    }`}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {cartEnabled && (
            <div className="mt-5 flex items-center gap-3">
              <input
                type="text"
                inputMode="numeric"
                min={1}
                value={qty}
                onChange={(e) => setQty(Math.max(1, Number(e.target.value.replace(',', '.')) || 1))}
                className="w-20 px-3 py-2 text-sm border border-wheat rounded-rustic bg-linen text-ink text-center"
              />
              <button
                onClick={async () => {
                  try {
                    await addItem(p.id, qty, selectedVariant?.id);
                    showToast(t('eshopAddedToCart'), 'success');
                  } catch {
                    showToast(t('eshopCartError'), 'error');
                  }
                }}
                disabled={!canAddToCart}
                className="btn-primary disabled:opacity-50"
              >
                {hasVariants && !selectedVariant ? t('eshopSelectVariant') : t('eshopAddToCart')}
              </button>
            </div>
          )}

          {p.shortDescription && (
            <p className="mt-4 text-wood">{p.shortDescription}</p>
          )}

          {p.attributes && Object.keys(p.attributes).length > 0 && (
            <div className="mt-6">
              <h2 className="text-sm font-semibold text-bark uppercase tracking-wide mb-2">{t('eshopAttributes')}</h2>
              <dl className="border border-wheat rounded-rustic divide-y divide-wheat bg-linen">
                {Object.entries(p.attributes).map(([k, v]) => (
                  <div key={k} className="flex px-3 py-2 text-sm">
                    <dt className="w-1/3 text-wood">{k}</dt>
                    <dd className="w-2/3 text-bark">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>

      {p.description && (
        <div className="mt-10">
          <h2 className="text-lg font-semibold text-bark mb-3">{t('eshopDescription')}</h2>
          <div
            className="prose prose-sm max-w-none text-ink"
            dangerouslySetInnerHTML={{ __html: p.description }}
          />
        </div>
      )}

      {/* Related products */}
      {p.related && p.related.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-semibold text-bark mb-3">{t('eshopRelated')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {p.related.map((r) => (
              <Link
                key={r.id}
                href={`/shop/${r.slug}`}
                className="border border-wheat rounded-rustic bg-linen p-3 hover:border-wood transition-colors"
              >
                <div className="aspect-square mb-2 bg-cream rounded overflow-hidden flex items-center justify-center">
                  {r.imageAssetId ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={`/api/public/eshop/assets/${r.imageAssetId}`} alt={r.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-4xl text-wheat">🛍️</span>
                  )}
                </div>
                <div className="text-sm font-medium text-bark line-clamp-2">{r.name}</div>
                {(r.groupPrice ?? r.price) != null && (
                  <div className="text-sm text-moss font-semibold mt-1">{fmt(r.groupPrice ?? r.price ?? 0, r.currency)}</div>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recently viewed (localStorage) */}
      {recent.filter((r) => r.slug !== p.slug).length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-semibold text-bark mb-3">{t('eshopRecentlyViewed')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {recent.filter((r) => r.slug !== p.slug).map((r) => (
              <Link
                key={r.slug}
                href={`/shop/${r.slug}`}
                className="border border-wheat rounded-rustic bg-linen p-3 hover:border-wood transition-colors"
              >
                <div className="aspect-square mb-2 bg-cream rounded overflow-hidden flex items-center justify-center">
                  {r.imageAssetId ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={`/api/public/eshop/assets/${r.imageAssetId}`} alt={r.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-4xl text-wheat">🛍️</span>
                  )}
                </div>
                <div className="text-sm font-medium text-bark line-clamp-2">{r.name}</div>
                {r.price != null && (
                  <div className="text-sm text-moss font-semibold mt-1">{fmt(r.price, r.currency)}</div>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Reviews */}
      {reviewsEnabled && (
        <ReviewsSection product={p} reviews={reviews || []} onSubmitted={() => mutateReviews()} />
      )}
    </div>
  );
}

/** Approved reviews + submit form (guest needs a name). */
function ReviewsSection({ product, reviews, onSubmitted }: {
  product: Product;
  reviews: Review[];
  onSubmitted: () => void;
}) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const [rating, setRating] = useState(5);
  const [authorName, setAuthorName] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiFetch(`/api/public/eshop/products/${product.id}/reviews`, {
        method: 'POST',
        body: JSON.stringify({ authorName: authorName || null, rating, body: body || null }),
      });
      showToast(t('eshopReviewSubmitted'), 'success');
      setBody('');
      setAuthorName('');
      setShowForm(false);
      onSubmitted();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : t('errSaveGeneric'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-bark">{t('eshopReviews')} ({reviews.length})</h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-secondary text-sm">
          {t('eshopWriteReview')}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-5 p-4 border border-wheat rounded-rustic bg-linen space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-wood">{t('eshopRating')}:</span>
            <Stars value={rating} onChange={setRating} />
          </div>
          {!isAuthenticated && (
            <input
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder={t('eshopYourName')}
              required
              className="w-full max-w-sm px-3 py-1.5 text-sm border border-wheat rounded-rustic bg-cream"
            />
          )}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('eshopReviewBody')}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-wheat rounded-rustic bg-cream"
          />
          <div>
            <button type="submit" disabled={busy} className="btn-primary text-sm disabled:opacity-50">
              {t('eshopSubmitReview')}
            </button>
            <span className="text-xs text-wood ml-3">{t('eshopReviewModeration')}</span>
          </div>
        </form>
      )}

      {reviews.length === 0 ? (
        <p className="text-sm text-wood">{t('eshopNoReviews')}</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="p-4 border border-wheat rounded-rustic bg-linen">
              <div className="flex items-center gap-2">
                <Stars value={r.rating} />
                <span className="text-sm font-medium text-bark">{r.authorName}</span>
                <span className="text-xs text-wood">{fmtDate(r.createdAt)}</span>
              </div>
              {r.body && <p className="text-sm text-ink mt-1.5 whitespace-pre-wrap">{r.body}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
